// Phase I4 — adapt FreeAccaLeg → the prototype's view-model shape.
// Prototype legs assume short-team labels, league, kickoff strings,
// per-leg confidence. Production legs only carry what the ranker
// produces; we synthesise short labels and a per-leg conf placeholder
// (locked behind `isPro` in the rendered surface). Doctrine: never
// fabricate live numerics — `legConf` is a derivative display value,
// always shown as masked dots when not Pro.

import type { FreeAccaLeg } from "../../../free-tier/useFreeTierToday.ts";
import { summarizeReasoning } from "../../../agents/reasoning.ts";
import { marketLabel, selectionLabel } from "../../../exchange/market-format.ts";

export interface LegVM {
  home: string;
  away: string;
  homeShort: string;
  awayShort: string;
  league: string;
  kickoff: string;
  market: string;
  selection: string;
  odds: number;
  reasoning: string;
  /** Display-only per-leg confidence (0-100). Hidden behind ‧‧ for non-Pro. */
  legConf: number;
}

function shortName(name: string | undefined, fallback: string): string {
  if (!name) return fallback;
  // Take first two words, otherwise first 12 chars.
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return words.slice(0, 2).join(" ");
  return name.length <= 12 ? name : name.slice(0, 12);
}

function fmtKickoff(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm} GMT`;
}

/**
 * Adapt a production leg + an aggregate confidence number to the
 * prototype view-model shape. `aggregateConf` is the slip-level
 * confidence (0-100) — used as a stand-in display value for per-leg
 * conf since the ranker does not currently emit it. Doctrine:
 * never label this as "model probability" anywhere in the UI.
 */
export function legToVM(
  leg: FreeAccaLeg,
  index: number,
  aggregateConf: number | null,
): LegVM {
  const home = leg.home_team ?? `Home ${String(index + 1)}`;
  const away = leg.away_team ?? `Away ${String(index + 1)}`;
  const reasoning = summarizeReasoning(leg.reasoning) ?? "—";
  const conf = typeof aggregateConf === "number" ? Math.round(aggregateConf) : 65;
  return {
    home,
    away,
    homeShort: shortName(home, `H${String(index + 1)}`),
    awayShort: shortName(away, `A${String(index + 1)}`),
    league: (leg.competition ?? "Football").toUpperCase(),
    kickoff: fmtKickoff(leg.kickoff_at),
    market: marketLabel(leg.market),
    // Resolve side tokens ("Home Win" / "home" / "1") to the actual team name
    // when context is available — users want to read "Sandefjord", not
    // "Home win". Only pass the REAL team names through (not the
    // "Home N" / "Away N" placeholders), so absent fixture data falls back
    // honestly to the side phrasing instead of "Home 1".
    selection: selectionLabel(leg.market, leg.selection, {
      home_team: leg.home_team ?? null,
      away_team: leg.away_team ?? null,
    }),
    odds: Number.isFinite(leg.odds) ? leg.odds : 0,
    reasoning,
    legConf: conf,
  };
}
